<script>
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import PhotoUploader from '@components/uploaders/photo-uploader.vue';
import { equipmentModelMixin } from '@mixins/models/equipment-model-mixin';
import {
  adminPanelComputed,
  adminPanelMethods,
  authComputed,
  equipmentComputed,
  equipmentMethods,
  mediaComputed,
  mediaMethods,
  modalMethods,
} from '@state/helpers';

export default {
  components: {
    PhotoUploader,
  },
  props: {
    content: {
      type: Object,
      required: false,
      default: null,
    },
  },
  mixins: [equipmentModelMixin],
  data() {
    return {
      iconPreview: null,
      photoPreview: null,
      typeOpts: [{ id: 1, label: 'Weight' }],
      unitOpts: [
        { id: 1, label: 'LBS' },
        { id: 2, label: 'KG' },
      ],
      weightedOpts: [
        { value: true, label: 'Weighted' },
        { value: false, label: 'Unweighted' },
      ],
      isEquipWeighted: null,
      formErr: null,
      hasChanges: false,
      initialEquipObj: null,
    };
  },
  computed: {
    ...adminPanelComputed,
    ...authComputed,
    ...equipmentComputed,
    ...mediaComputed,
    equipmentObj() {
      return this.selectedEquipment
        ? cloneDeep(this.selectedEquipment)
        : this.getEquipmentModel();
    },
    coverPhotoUrl() {
      return this.equipmentObj.mediaAssets &&
        this.equipmentObj.mediaAssets.coverPhoto &&
        this.equipmentObj.mediaAssets.coverPhoto.url
        ? this.equipmentObj.mediaAssets.coverPhoto.url
        : null;
    },
    thumbnailPhotoUrl() {
      return this.equipmentObj.mediaAssets &&
        this.equipmentObj.mediaAssets.thumbnailPhoto &&
        this.equipmentObj.mediaAssets.thumbnailPhoto.url
        ? this.equipmentObj.mediaAssets.thumbnailPhoto.url
        : null;
    },
    weightedOptsVal() {
      return this.weightedOpts.find(
        (opts) => opts.value === this.equipmentObj.isWeighted
      );
    },
    isWeighted() {
      return this.equipmentObj && this.equipmentObj.isWeighted;
    },
  },
  mounted() {},
  methods: {
    ...adminPanelMethods,
    ...equipmentMethods,
    ...mediaMethods,
    ...modalMethods,
    isFormValid() {
      if (!Object.keys(this.equipmentObj).length) {
        this.formErr = {};
        this.formErr.titleErr = this.equipmentObj.label
          ? null
          : 'Title is required';
        this.formErr.descErr = this.equipmentObj.description
          ? null
          : 'Description is required';
        return false;
      } else {
        this.formErr = null;
        return true;
      }
    },
    setIsWeightedEquip(evt) {
      this.isEquipWeighted = evt.value;
      this.equipmentObj.isWeighted = evt.value;
    },
    clearForm() {
      this.setSelectedEquipment(null);
      this.isEquipWeighted = false;
      this.formErr = null;
      this.hasChanges = false;
    },
    assignMediaUrls() {
      this.equipmentObj.mediaAssets = {
        coverPhoto: {
          url: this.hasNewMedia('cover', 'equipments')
            ? this.hasNewMedia('cover', 'equipments').publicUrl
            : null,
        },
        thumbnailPhoto: {
          url: this.hasNewMedia('thumbnail', 'equipments')
            ? this.hasNewMedia('thumbnail', 'equipments').publicUrl
            : null,
        },
      };
    },
    async buildEquipmentPayload() {
      await this.assignMediaUrls();
      return { ...this.equipmentObj, org_id: this.userOrgId };
    },
    confirmDelete() {
      this.setSelectedItem({ ...this.selectedEquipment, context: 'Equipment' });
      this.$bvModal.show('confirmation-modal');
    },
    handleSave() {
      this.selectedEquipment ? this.update() : this.create();
    },
    async create() {
      if (this.isFormValid()) {
        const payload = await this.buildEquipmentPayload();
        this.createEquipment({ equipmentObj: payload })
          .then((res) => {
            if (res.success) {
              this.setHideAdminPanel();
              this.clearForm();
              this.clearMedia();
            }
          })
          .catch((e) => console.log(e));
      }
    },
    handleUpdate(payload) {
      this.updateEquipment({ equipmentObj: payload })
        .then((res) => {
          if (res.success) {
            this.setHideAdminPanel();
            this.clearForm();
            this.clearMedia();
          }
        })
        .catch((e) => console.log(e));
    },
    async update() {
      const payload = await this.buildEquipmentPayload();
      this.handleUpdate(payload);
    },
  },
  watch: {
    isAdminPanelActive(newVal, oldVal) {
      this.initialEquipObj = this.selectedEquipment
        ? cloneDeep(this.selectedEquipment)
        : cloneDeep(this.getEquipmentModel());
      if (newVal === false) {
        this.clearForm();
      }
    },
    equipmentObj: {
      deep: true,
      handler(newVal, oldVal) {
        if (newVal._id === oldVal._id) {
          this.hasChanges = !isEqual(this.initialEquipObj, this.equipmentObj);
        }
      },
    },
  },
};
</script>

<template>
  <form @submit.prevent="save">
    <div class="row">
      <div class="col">
        <label for="title">Title</label>
        <BaseInputText
          :class="[{ 'input-error': formErr && formErr.titleErr }]"
          v-model="equipmentObj.label"
          name="title"
          placeholder="Kettlebell"
          :error="formErr && formErr.titleErr"
        />
      </div>
    </div>

    <div class="row">
      <div class="col">
        <label for="type">Type</label>
        <v-select
          :options="weightedOpts"
          :searchable="false"
          :clearable="false"
          :value="weightedOptsVal"
          @input="setIsWeightedEquip"
        />
      </div>
    </div>

    <div v-if="isEquipWeighted || isWeighted" class="row">
      <div class="col">
        <label for="min_weight">Min Weight</label>
        <BaseInputText
          v-model="equipmentObj.minWeight"
          type="number"
          name="min_weight"
          placeholder="0"
        />
      </div>
      <div class="col">
        <label for="min_weight">Max Weight</label>
        <BaseInputText
          v-model="equipmentObj.maxWeight"
          type="number"
          name="max_weight"
          placeholder="0"
        />
      </div>
    </div>

    <div class="row">
      <div class="col">
        <label for="description">Description</label>
        <BaseInputTextarea
          :class="[{ 'input-error': formErr && formErr.titleErr }]"
          v-model="equipmentObj.description"
          name="description"
          placeholder="Hand-held weight that looks like a kettle"
          :error="formErr && formErr.descErr"
        />
      </div>
    </div>

    <div class="row">
      <div class="col">
        <label>Photo</label>
        <div class="helper-text">
          The photo will be used primarily on the admin side, not the user
          interface.
        </div>

        <PhotoUploader
          requestType="equipments"
          class="photo-uploader"
          :existingPhoto="coverPhotoUrl"
          :hidePlaceholder="true"
          type="cover"
          helper-text="Photos must be .jpg, .jpeg, .gif, or .webp, no larger than 4MB. Square
          images work best."
        />
      </div>
    </div>

    <div class="row">
      <div class="col">
        <label>Icon</label>
        <div class="helper-text">
          The photo will be used primarily on the admin side, not the user
          interface.
        </div>

        <PhotoUploader
          requestType="equipments"
          class="photo-uploader"
          :hidePlaceholder="true"
          :existingPhoto="thumbnailPhotoUrl"
          type="thumbnail"
        />
      </div>
    </div>

    <!-- TODO: Leverage PanelFooter slots (/footer/panel-footer.vue) -->
    <div :class="['panel-footer', { 'form-error': formErr }]">
      <span class="action-title">
        <span v-if="hasChanges">Unsaved Changes</span>
      </span>
      <div class="btn-group">
        <BaseButton
          v-if="selectedEquipment"
          type="button"
          class="btn-primary btn-clear"
          @click="confirmDelete"
        >
          <BaseIcon name="trash" />
          Delete
        </BaseButton>
        <BaseButton
          type="button"
          class="btn-primary btn-clear"
          @click="clearForm"
          v-if="!selectedEquipment"
        >
          <BaseIcon name="times" />
          Cancel
        </BaseButton>
        <span class="divider-vertical">&nbsp;</span>
        <BaseButton
          type="button"
          :class="[
            'btn-primary btn-clear',
            { 'new-item': selectedEquipment, changed: hasChanges },
          ]"
          @click="handleSave"
        >
          <BaseIcon name="save" />
          <span v-if="selectedEquipment">Save</span>
          <span v-else>Create</span> Equipment
        </BaseButton>
      </div>
    </div>
  </form>
</template>

<style lang="scss" scoped>
@import '@design';

.panel-footer {
  align-items: center;
  background-color: $color-header-bg;
  border-radius: $size-input-border-radius;
  bottom: 1rem;
  color: white;
  display: flex;
  justify-content: space-between;
  padding: 1rem 2rem;
  position: fixed;
  width: 94%;
  z-index: 9999;

  &.form-error {
    background-color: $color-error-dark;
  }

  .action-title .link-deselect {
    margin-left: 1rem;
    opacity: 0.5;
    text-decoration: underline;
    transition: opacity $duration-animation-base;

    &:hover {
      cursor: pointer;
      opacity: 1;
    }
  }

  .divider-vertical {
    border-left: 1px solid white;
    height: 3.5rem;
  }

  .btn-clear {
    color: white;

    &:active,
    &:focus {
      color: white;
    }

    &:last-of-type {
      opacity: 0.6;

      &.changed {
        opacity: 1;
      }

      &.new-item {
        opacity: 1;
      }
    }

    span {
      margin-left: 0.5rem;
    }
  }
}

.photo-uploader {
  margin-bottom: 10px;
}
</style>
